import { Col, Form, Input, Row, Select } from "antd";
import { useTranslation } from "react-i18next";
import {
  AuthType,
  IntegrationMethod,
  IntegrationProtocol,
} from "../../../api/models";
import { useFeatureFlag } from "../../../contexts/featureFlagContext";
import { validateBase64 } from "../../../util/validation";

interface FormItemProps {
  formDisabled: boolean;
}

export const HttpFormItems = ({ formDisabled }: FormItemProps) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const authType = Form.useWatch("authType", form);

  // Feature flag to control visibility of email protocol. Flag set by file public/feature-flags.json.
  const showEmailProtocol = useFeatureFlag("showEmailProtocol");

  const getAuthValuePlaceholder = (authType: AuthType | undefined) => {
    switch (authType) {
      case AuthType.Basic:
        return t("integrations:auth-value-placeholder");
      case AuthType.Bearer:
        return "access/api key";
      default:
        return "";
    }
  };

  const validateAuthValue = (rule: any, value: string) => {
    if (!value) {
      return Promise.resolve();
    }

    if (!validateBase64(value)) {
      return Promise.reject(
        new Error(t("integrations:auth-value-validation-error"))
      );
    }

    return Promise.resolve();
  };

  return (
    <div data-testid="http-form-items">
      <Row>
        <Col>
          <div
            style={{
              display: "flex",
              gap: "4em",
            }}
          >
            <Form.Item
              label={<b>{t("integrations:integration-name")}</b>}
              name="integrationName"
              rules={[{ required: true, message: t("translations:required") }]}
            >
              <Input style={{ width: "175px" }} disabled={formDisabled} />
            </Form.Item>
            <Form.Item
              label={<b>{t("integrations:integration-type")}</b>}
              name="integrationProtocol"
              rules={[{ required: true, message: t("translations:required") }]}
            >
              {/* Feature flag added to hide email protocol until supported */}
              <Select style={{ width: "175px" }} disabled={formDisabled}>
                {showEmailProtocol.enabled ? (
                  Object.values(IntegrationProtocol).map((protocol) => (
                    <Select.Option key={protocol} value={protocol}>
                      {protocol}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option
                    key={IntegrationProtocol.HTTP}
                    value={IntegrationProtocol.HTTP}
                  >
                    {IntegrationProtocol.HTTP}
                  </Select.Option>
                )}
              </Select>
            </Form.Item>
          </div>
        </Col>
      </Row>
      <div
        style={{
          display: "flex",
          gap: "4em",
        }}
      >
        <Form.Item
          label={<b>{t("integrations:http-method")}</b>}
          name="httpMethod"
          rules={[{ required: true, message: t("translations:required") }]}
        >
          <Select style={{ width: "175px" }} disabled={formDisabled}>
            {Object.values(IntegrationMethod).map((method) => (
              <Select.Option key={method} value={method}>
                {method}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={<b>{t("integrations:endpoint-url")}</b>}
          style={{ flex: 1 }}
          name="endpointUrl"
          rules={[{ required: true, message: t("translations:required") }]}
        >
          <Input
            style={{ width: "100%", maxWidth: "415px" }}
            disabled={formDisabled}
            placeholder="https://example.com/api/endpoint"
          />
        </Form.Item>
      </div>
      <div
        style={{
          display: "flex",
          gap: "4em",
        }}
      >
        <Form.Item
          label={<b>{t("integrations:auth-type")}</b>}
          name="authType"
          rules={[{ required: true, message: t("translations:required") }]}
        >
          <Select style={{ width: "175px" }} disabled={formDisabled}>
            {Object.values(AuthType).map((type) => (
              <Select.Option key={type} value={type}>
                {type}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={<b>{t("integrations:auth-value")}</b>}
          style={{ flex: 1 }}
          name="authValue"
          rules={[
            { required: true, message: t("translations:required") },
            { validator: validateAuthValue },
          ]}
          data-testid="auth-value-form-item"
        >
          <Input
            style={{ width: "100%", maxWidth: "415px" }}
            disabled={formDisabled}
            placeholder={getAuthValuePlaceholder(authType)}
          />
        </Form.Item>
      </div>
    </div>
  );
};
